import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useRole } from "../context/RoleContext";
import { listAdminUsers, adminExportUrl } from "../lib/adminPortal";
import { Download, Search } from "lucide-react";
import { relativeDate } from "../lib/ownerUi";
import ListTable, { ListRow } from "../components/ListTable";

const ROLE_FILTERS = [
  { v: "all", label: "All" },
  { v: "owner_builder", label: "Owner / Builder" },
  { v: "architect", label: "Architect" },
  { v: "city_staff", label: "City Staff" },
  { v: "platform_admin", label: "Platform Admin" },
  { v: "deactivated", label: "Deactivated" },
];

const ROLE_TONE = {
  owner_builder: { fg: "#1B1F4B", bg: "rgba(91,97,150,0.12)" },
  architect: { fg: "#8F3C23", bg: "rgba(177,78,47,0.10)" },
  city_staff: { fg: "#3A5563", bg: "rgba(58,85,99,0.10)" },
  platform_admin: { fg: "#1B1F4B", bg: "rgba(27,31,75,0.10)" },
};

export default function AdminUsers() {
  const { user } = useRole();
  const [role, setRole] = useState("all");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    const t = setTimeout(() => {
      listAdminUsers(user.id, { role, q }).then((d) => alive && setData(d));
    }, 200);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [user?.id, role, q]);

  return (
    <section className="animate-fade-in-up" data-testid="admin-users-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <span aria-hidden style={{ display: "inline-block", width: 28, height: 2, background: "#B14E2F" }} />
            <span className="wn-label" style={{ color: "#8F3C23" }}>Platform Admin · User Management</span>
          </div>
          <h1
            className="mt-3"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontSize: "clamp(26px, 3.6vw, 32px)",
              fontWeight: 600,
              color: "#1B1F4B",
            }}
          >
            Users.
          </h1>
          {data && (
            <p style={{ color: "#6C6B85", fontSize: 13, marginTop: 4 }}>
              <span className="tabular-nums" style={{ color: "#272636", fontWeight: 700 }}>{data.counts.all}</span> total ·{" "}
              <span className="tabular-nums">{data.counts.owner_builder}</span> owners ·{" "}
              <span className="tabular-nums">{data.counts.architect}</span> architects ·{" "}
              <span className="tabular-nums">{data.counts.city_staff}</span> city ·{" "}
              <span className="tabular-nums">{data.counts.platform_admin}</span> admin ·{" "}
              <span className="tabular-nums" style={{ color: data.counts.deactivated > 0 ? "#A23B2E" : "inherit" }}>
                {data.counts.deactivated}
              </span>{" "}
              deactivated
            </p>
          )}
        </div>
        <a
          href={adminExportUrl("users", user?.id, { role, q })}
          className="wn-btn wn-btn-tertiary"
          data-testid="admin-users-export"
        >
          <Download size={14} /> Export user list (CSV)
        </a>
      </div>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap" data-testid="admin-users-filters">
          {ROLE_FILTERS.map((f) => {
            const active = role === f.v;
            return (
              <button
                key={f.v}
                onClick={() => setRole(f.v)}
                data-testid={`admin-users-filter-${f.v}`}
                style={{
                  padding: "5px 12px",
                  borderRadius: 999,
                  border: "1px solid " + (active ? "#1B1F4B" : "#D9D8E4"),
                  background: active ? "#1B1F4B" : "transparent",
                  color: active ? "#FBFAFD" : "#272636",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {f.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 ml-auto" style={{ minWidth: 260 }}>
          <Search size={14} style={{ color: "#6C6B85" }} />
          <input
            className="wn-input"
            placeholder="Name, email, organisation"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="admin-users-search"
            style={{ flex: 1 }}
          />
        </div>
      </div>

      <div style={{ borderTop: "1px solid #D9D8E4", marginTop: 16 }} />

      <ListTable
        testId="admin-users"
        columns={[
          { label: "User" },
          { label: "Role" },
          { label: "Status" },
          { label: "Last active", align: "right" },
        ]}
        gridTemplate={USER_GRID}
        gap={USER_GAP}
        items={data?.items}
        emptyTitle="No users match this filter."
        loadingHeight={56}
        renderRow={(u, _i, isLast) => (
          <UserRow key={u.id} u={u} isLast={isLast} />
        )}
      />
    </section>
  );
}

/* Shared column template: User · Role · Status · Last active */
const USER_GRID = "minmax(0, 2.2fr) 140px 120px 160px";
const USER_GAP = 18;

function UserRow({ u, isLast }) {
  const tone = ROLE_TONE[u.role] || ROLE_TONE.owner_builder;
  const isDeactivated = u.account_status === "deactivated";
  return (
    <ListRow
      as={Link}
      to={`/admin/users/${u.id}`}
      gridTemplate={USER_GRID}
      gap={USER_GAP}
      isLast={isLast}
      data-testid={`admin-user-row-${u.id}`}
    >
      <div className="min-w-0">
        <div
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 600,
            fontSize: 15,
            color: "#1B1F4B",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {u.name}
        </div>
        <div
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 12,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {u.organization || u.firm || "—"}
          {u.email && (
            <span style={{ fontStyle: "normal", color: "#272636", marginLeft: 6 }}>
              · {u.email}
            </span>
          )}
        </div>
      </div>
      <span
        style={{
          padding: "2px 8px",
          borderRadius: 999,
          background: tone.bg,
          color: tone.fg,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
          justifySelf: "start",
        }}
      >
        {u.role.replace(/_/g, " ")}
      </span>
      <span
        style={{
          padding: "2px 8px",
          borderRadius: 999,
          background: isDeactivated ? "rgba(154,160,192,0.30)" : "rgba(79,97,73,0.10)",
          color: isDeactivated ? "#6C6B85" : "#4F6149",
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
          justifySelf: "start",
        }}
        data-testid={`user-status-${u.id}`}
      >
        {isDeactivated ? "Deactivated" : "Active"}
      </span>
      <span
        className="tabular-nums"
        style={{
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
          color: "#6C6B85",
          fontSize: 12,
          textAlign: "right",
          whiteSpace: "nowrap",
        }}
      >
        {u.last_active_at ? relativeDate(u.last_active_at) : "—"}
      </span>
    </ListRow>
  );
}
